import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { db } from '../config/firebase';
import { collection, doc, updateDoc, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { 
  MatrixGraduationCap,
  MatrixCamera,
  MatrixChart,
  MatrixTarget,
  MatrixPaperclip,
  MatrixDownload,
  MatrixLightbulb,
  MatrixFile,
  MatrixLink,
  MatrixEye,
  MatrixTrash,
  MatrixAdmin,
  MatrixClock,
  MatrixCheckCircle,
  MatrixXCircle
} from '../components/MatrixEmojis';

// Student Details Modal Component (Keeping the existing one but with glassmorphism enhancements)
const StudentDetailsModal = ({ student, darkMode, isOpen, onClose, setSelectedImage, setImageModalOpen, setSelectedPdf, setPdfModalOpen }) => {
  if (!isOpen || !student) return null;

  // Log the full student object for debugging
  console.log('StudentDetailsModal: Full student object =', student);
  console.log('StudentDetailsModal: student.files =', student.files);
  if (student.files?.photo) {
    console.log('StudentDetailsModal: student.files.photo =', student.files.photo);
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const renderFilePreview = (fileData, fileName, fileType) => {
    console.log(`renderFilePreview called for ${fileName}:`, fileData);
    
    // Handle multiple possible file URL formats
    let finalUrl = null;
    
    if (typeof fileData === 'string' && fileData) {
      finalUrl = fileData;
    } else if (fileData && typeof fileData === 'object') {
      // Handle different object structures
      finalUrl = fileData.url || fileData.secure_url || fileData.downloadURL || fileData.publicUrl;
    }
    
    console.log(`${fileName} - final URL:`, finalUrl);
    
    if (!finalUrl) {
      return <span className={`text-gray-500 italic ${darkMode ? 'text-gray-400' : ''}`}>Not uploaded</span>;
    }

    const handleViewFile = (e) => {
      e.preventDefault();
      
      if (fileType === 'image' || finalUrl.includes('.jpg') || finalUrl.includes('.jpeg') || finalUrl.includes('.png') || finalUrl.includes('placeholder')) {
        setSelectedImage({ url: finalUrl, name: fileName });
        setImageModalOpen(true);
      } else if (finalUrl.includes('.pdf') || fileType === 'pdf') {
        setSelectedPdf({ url: finalUrl, name: fileName });
        setPdfModalOpen(true);
      } else {
        window.open(finalUrl, '_blank');
      }
    };

    return (
      <div className="flex flex-col space-y-1">
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleViewFile}
            className={`text-blue-500 hover:text-blue-700 underline flex items-center space-x-1 transition-colors duration-200 ${
              darkMode ? 'text-blue-400 hover:text-blue-300' : ''
            }`}
          >
            <MatrixEye size="16" />
            <span>View {fileName}</span>
          </button>
          <a 
            href={finalUrl} 
            download
            className={`text-green-500 hover:text-green-700 underline flex items-center space-x-1 transition-colors duration-200 ${
              darkMode ? 'text-green-400 hover:text-green-300' : ''
            }`}
          >
            <MatrixDownload size="16" />
            <span>Download</span>
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Glassmorphism Backdrop */}
      <div 
        className="fixed inset-0 backdrop-blur-md bg-black/30"
        onClick={onClose}
      ></div>
      
      {/* Modal Content */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full max-w-4xl rounded-3xl shadow-2xl ${
          darkMode 
            ? 'bg-gray-800/90 backdrop-blur-xl border border-gray-700/50' 
            : 'bg-white/90 backdrop-blur-xl border border-white/20'
        }`}>
          
          {/* Header */}
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700/50' : 'border-gray-200/50'}`}>            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {student.files?.photo?.thumbnailUrl || student.files?.photo?.optimizedUrl || student.files?.photo?.url ? (
                  <img 
                    className="h-16 w-16 rounded-full object-cover border-2 border-gray-300 shadow-lg" 
                    src={student.files.photo.thumbnailUrl || student.files.photo.optimizedUrl || student.files.photo.url} 
                    alt={student.name}
                  />
                ) : (
                  <div className={`h-16 w-16 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'} flex items-center justify-center shadow-lg`}>
                    <span className={`text-lg font-bold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {student.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
                <div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {student.name}
                  </h2>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Application ID: {student.applicationId || student._id}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-full hover:bg-gray-100 ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'text-gray-600'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Personal Information */}
              <div className={`p-4 rounded-xl ${
                darkMode 
                  ? 'bg-gray-700/50 backdrop-blur-sm border border-gray-600/30' 
                  : 'bg-gray-50/80 backdrop-blur-sm border border-gray-200/50'
              }`}>
                <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Personal Information
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email:</span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-right break-all`}>{student.email || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone:</span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{student.phone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Address:</span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-right break-words`}>{student.address || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div className={`p-4 rounded-xl ${
                darkMode 
                  ? 'bg-gray-700/50 backdrop-blur-sm border border-gray-600/30' 
                  : 'bg-gray-50/80 backdrop-blur-sm border border-gray-200/50'
              }`}>
                <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Academic Details
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category/Caste:</span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{student.category || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>NEET Score:</span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{student.neetScore || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      student.status === 'Approved' 
                        ? 'bg-green-100 text-green-800' 
                        : student.status === 'Rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {student.status || 'Pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className={`lg:col-span-2 p-4 rounded-xl ${
                darkMode 
                  ? 'bg-gray-700/50 backdrop-blur-sm border border-gray-600/30' 
                  : 'bg-gray-50/80 backdrop-blur-sm border border-gray-200/50'
              }`}>
                <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {student.files && Object.entries(student.files).map(([key, file]) => (
                    <div key={key} className="space-y-1">
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} capitalize`}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}:
                      </label>
                      {renderFilePreview(file, key, key.includes('photo') ? 'image' : 'pdf')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Image Modal Component
// Enhanced Image Modal Component with zoom and fullscreen
const EnhancedImageModal = ({ image, isOpen, onClose, darkMode }) => {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setIsFullscreen(false);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, isFullscreen, onClose]);

  if (!isOpen || !image) return null;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.5, 0.1));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const modalClasses = isFullscreen 
    ? "fixed inset-0 z-[60] bg-black"
    : "fixed inset-0 z-50 overflow-hidden p-2 sm:p-4";

  const contentClasses = isFullscreen
    ? "w-full h-full flex flex-col"
    : `relative w-full max-w-6xl max-h-[95vh] rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden ${
        darkMode 
          ? 'bg-gray-800/95 backdrop-blur-xl border border-gray-700/50' 
          : 'bg-white/95 backdrop-blur-xl border border-white/20'
      }`;

  return (
    <div className={modalClasses}>
      {/* Backdrop */}
      {!isFullscreen && (
        <div 
          className="fixed inset-0 backdrop-blur-md bg-black/50"
          onClick={onClose}
        />
      )}
      
      {/* Modal Content */}
      <div className={isFullscreen ? "w-full h-full" : "flex min-h-full items-center justify-center"}>
        <div className={contentClasses}>
          
          {/* Header */}
          <div className={`px-3 sm:px-6 py-3 sm:py-4 flex-shrink-0 ${
            isFullscreen 
              ? 'bg-black/90 text-white border-b border-gray-600'
              : `border-b ${darkMode ? 'border-gray-700/50' : 'border-gray-200/50'}`
          }`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-sm sm:text-lg font-semibold flex items-center space-x-1 sm:space-x-2 ${
                isFullscreen ? 'text-white' : (darkMode ? 'text-white' : 'text-gray-900')
              }`}>
                <MatrixCamera size="24" />
                <span className="truncate">{image.name}</span>
                <span className="hidden sm:inline text-xs sm:text-sm font-normal opacity-75">
                  (Zoom: {Math.round(zoom * 100)}%)
                </span>
              </h3>
              
              {/* Control Buttons */}
              <div className="flex items-center space-x-1 sm:space-x-2">
                {/* Zoom Out */}
                <button
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.1}
                  className={`p-2 sm:p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                    zoom <= 0.1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95'
                  } ${isFullscreen ? 'text-white hover:bg-white/10' : (darkMode ? 'text-gray-400' : 'text-gray-600')}`}
                  title="Zoom Out"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>

                {/* Reset Zoom */}
                <button
                  onClick={handleResetZoom}
                  className={`p-2 sm:p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95 touch-manipulation ${
                    isFullscreen ? 'text-white hover:bg-white/10' : (darkMode ? 'text-gray-400' : 'text-gray-600')
                  }`}
                  title="Reset Zoom"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Zoom In */}
                <button
                  onClick={handleZoomIn}
                  disabled={zoom >= 5}
                  className={`p-2 sm:p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                    zoom >= 5 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95'
                  } ${isFullscreen ? 'text-white hover:bg-white/10' : (darkMode ? 'text-gray-400' : 'text-gray-600')}`}
                  title="Zoom In"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>

                {/* Fullscreen Toggle */}
                <button
                  onClick={handleFullscreen}
                  className={`p-2 sm:p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95 touch-manipulation ${
                    isFullscreen ? 'text-white hover:bg-white/10' : (darkMode ? 'text-gray-400' : 'text-gray-600')
                  }`}
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  )}
                </button>

                {/* Close */}
                <button
                  onClick={isFullscreen ? handleFullscreen : onClose}
                  className={`p-2 sm:p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95 touch-manipulation ${
                    isFullscreen ? 'text-white hover:bg-white/10' : (darkMode ? 'text-gray-400' : 'text-gray-600')
                  }`}
                  title={isFullscreen ? "Exit Fullscreen" : "Close"}
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Image Content */}
          <div 
            className={`flex-1 overflow-hidden ${
              isFullscreen ? 'bg-black' : 'p-2 sm:p-4'
            }`}
            style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div className={`${isFullscreen ? 'h-full' : 'max-h-[60vh] sm:max-h-[70vh]'} flex items-center justify-center`}>
              <img 
                src={image.url} 
                alt={image.name}
                className="max-w-none rounded-lg shadow-lg transition-transform duration-200"
                style={{ 
                  transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                  maxWidth: isFullscreen ? '100vw' : '100%',
                  maxHeight: isFullscreen ? '100vh' : '60vh',
                  objectFit: 'contain'
                }}
                draggable={false}
                onDoubleClick={zoom === 1 ? handleZoomIn : handleResetZoom}
              />
            </div>
          </div>

          {/* Footer */}
          {!isFullscreen && (
            <div className={`px-3 sm:px-6 py-3 sm:py-4 border-t flex-shrink-0 ${
              darkMode ? 'border-gray-700/50' : 'border-gray-200/50'
            }`}>
              <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-3">
                <a
                  href={image.url}
                  download
                  className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-all duration-200 text-center touch-manipulation ${
                    darkMode 
                      ? 'bg-blue-600/80 hover:bg-blue-500/90 text-white backdrop-blur-sm border border-blue-400/30' 
                      : 'bg-blue-500/90 hover:bg-blue-600/90 text-white backdrop-blur-sm border border-blue-300/30'
                  } shadow-lg`}
                >
                  <MatrixDownload size="18" /> Download Image
                </a>
                <button
                  onClick={onClose}
                  className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-all duration-200 touch-manipulation ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Close
                </button>
              </div>
              <div className={`text-center mt-2 sm:mt-3 text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} px-2`}>
                <MatrixLightbulb size="18" /> <span className="hidden sm:inline">Double-click to zoom • Drag to pan when zoomed • ESC to close</span>
                <span className="sm:hidden">Double-tap to zoom • ESC to close</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// PDF Modal Component
const PdfModal = ({ pdf, isOpen, onClose, darkMode }) => {
  if (!isOpen || !pdf) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 backdrop-blur-md bg-black/50" onClick={onClose}></div>
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full max-w-6xl max-h-[90vh] rounded-2xl overflow-hidden ${
          darkMode ? 'bg-gray-800/90 backdrop-blur-xl' : 'bg-white/90 backdrop-blur-xl'
        }`}>
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {pdf.name}
              </h3>
              <div className="flex items-center gap-2">
                <a 
                  href={pdf.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Open in New Tab
                </a>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <iframe 
            src={pdf.url} 
            className="w-full h-[70vh]"
            title={pdf.name}
          />
        </div>
      </div>
    </div>
  );
};

// Application Card Component with Apple-style Glassmorphism
const ApplicationCard = ({ student, darkMode, onViewDetails, onAccept, onReject, onArchive, actionLoading, isArchived = false, onRestore, onPermanentDelete }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg shadow-green-500/30';
      case 'rejected':
        return 'bg-gradient-to-r from-red-400 to-rose-500 text-white shadow-lg shadow-red-500/30';
      case 'pending':
      default:
        return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-lg shadow-yellow-500/30';
    }
  };

  const studentId = student._id || student.applicationId;
  const currentActionLoading = actionLoading?.[studentId];

  return (
    <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-2xl p-4 sm:p-6 shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer`}
         onClick={() => onViewDetails(student)}>
        {/* Student Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-4">
        {student.files?.photo?.thumbnailUrl || student.files?.photo?.optimizedUrl || student.files?.photo?.url ? (
          <img 
            src={student.files.photo.thumbnailUrl || student.files.photo.optimizedUrl || student.files.photo.url} 
            alt={student.name}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white/20 shadow-lg flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0">
            {student.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'} truncate`}>
            {student.name}
          </h3>
          <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} truncate`}>
            ID: {student.applicationId || student._id}
          </p>
        </div>
        
        <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(student.status)} transform hover:scale-105 transition-all duration-300 flex-shrink-0`}>
          {student.status || 'Pending'}
        </div>
      </div>

      {/* Student Info */}
      <div className="space-y-3">
        <div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
          <div className="min-w-0">
            <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
              Email
            </p>
            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'} break-all`}>
              {student.email || 'N/A'}
            </p>
          </div>
          <div className="min-w-0">
            <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
              Phone
            </p>
            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {student.phone || 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="min-w-0">
          <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
            Category / NEET
          </p>
          <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'} break-words`}>
            {student.category || 'N/A'} / {student.neetScore || 'N/A'}
          </p>
        </div>

        {/* Show archive date if applicable */}
        {isArchived && student.archivedAt && (
          <div>
            <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
              Archived On
            </p>
            <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {new Date(student.archivedAt).toLocaleDateString()}
            </p>
          </div>
        )}

        {(student.createdAt || student.submittedAt) && (
          <div>
            <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
              Submitted
            </p>
            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {new Date(student.createdAt || student.submittedAt).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true
              })}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(student);
          }}
          className={`flex-1 min-w-0 py-3 px-4 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 touch-manipulation ${
            darkMode 
              ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30' 
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          <MatrixEye size="16" className="inline mr-2" />
          <span className="truncate">View Details</span>
        </button>
        
        {/* Active Applications Actions */}
        {!isArchived && (
          <>
            {student.status === 'Pending' && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAccept(studentId);
                  }}
                  disabled={currentActionLoading === 'accepting'}
                  className={`py-3 px-4 min-w-[44px] rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 touch-manipulation ${
                    currentActionLoading === 'accepting'
                      ? 'opacity-50 cursor-not-allowed'
                      : darkMode 
                        ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title="Accept Application"
                >
                  {currentActionLoading === 'accepting' ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <MatrixCheckCircle size="16" />
                  )}
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject(studentId);
                  }}
                  disabled={currentActionLoading === 'rejecting'}
                  className={`py-3 px-4 min-w-[44px] rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 touch-manipulation ${
                    currentActionLoading === 'rejecting'
                      ? 'opacity-50 cursor-not-allowed'
                      : darkMode 
                        ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                  title="Reject Application"
                >
                  {currentActionLoading === 'rejecting' ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <MatrixXCircle size="16" />
                  )}
                </button>
              </>
            )}
            
            {/* Archive button for all non-archived applications */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive(studentId);
              }}
              disabled={currentActionLoading === 'archiving'}
              className={`py-3 px-4 min-w-[44px] rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 touch-manipulation ${
                currentActionLoading === 'archiving'
                  ? 'opacity-50 cursor-not-allowed'
                  : darkMode 
                    ? 'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
              title="Move to Archive (Safe Delete - Can be Restored)"
            >
              {currentActionLoading === 'archiving' ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <MatrixTrash size="16" />
              )}
            </button>
          </>
        )}
        
        {/* Archived Applications Actions */}
        {isArchived && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRestore(studentId);
              }}
              disabled={currentActionLoading === 'restoring'}
              className={`py-3 px-4 min-w-[44px] rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 touch-manipulation ${
                currentActionLoading === 'restoring'
                  ? 'opacity-50 cursor-not-allowed'
                  : darkMode 
                    ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              title="Restore from Archive"
            >
              {currentActionLoading === 'restoring' ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPermanentDelete(studentId);
              }}
              disabled={currentActionLoading === 'deleting'}
              className={`py-3 px-4 min-w-[44px] rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 touch-manipulation ${
                currentActionLoading === 'deleting'
                  ? 'opacity-50 cursor-not-allowed'
                  : darkMode 
                    ? 'bg-red-600/20 text-red-300 hover:bg-red-600/30' 
                    : 'bg-red-200 text-red-800 hover:bg-red-300'
              }`}
              title="Permanently Delete (Cannot be Undone - Student Can Reapply)"
            >
              {currentActionLoading === 'deleting' ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <MatrixTrash size="16" />
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboardPage = ({ darkMode }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [archivedStudents, setArchivedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentView, setCurrentView] = useState('active'); // 'active' or 'archive'
  const [actionLoading, setActionLoading] = useState({});
  const [debugMode, setDebugMode] = useState(false);
  
  // Auto-clear success messages after 5 seconds
  useEffect(() => {
    if (error && (error.includes('successfully') || error.includes('Firebase'))) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Note: Local storage removed from admin dashboard to prevent data inconsistencies
  
  // Backend endpoint testing function
  const testBackendEndpoints = async () => {
    console.log('🧪 Testing available backend endpoints...');
    const sampleStudentId = students.length > 0 ? (students[0]._id || students[0].applicationId) : 'test-id';
    
    const endpoints = [
      { method: 'GET', url: `${API_BASE_URL}/api/admin/students`, description: 'Get all students' },
      { method: 'PUT', url: `${API_BASE_URL}/api/admin/students/${sampleStudentId}/status`, description: 'Update status' },
      { method: 'PUT', url: `${API_BASE_URL}/api/admin/students/${sampleStudentId}`, description: 'Update student' },
      { method: 'PATCH', url: `${API_BASE_URL}/api/admin/students/${sampleStudentId}`, description: 'Patch student' },
      { method: 'DELETE', url: `${API_BASE_URL}/api/admin/students/${sampleStudentId}`, description: 'Delete student' },
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🧪 Testing ${endpoint.method} ${endpoint.url}`);
        if (endpoint.method === 'GET') {
          const response = await axios.get(endpoint.url);
          console.log(`✅ ${endpoint.description}: ${response.status} - Available`);
        } else {
          // For other methods, we'll just check if they respond (even with errors)
          console.log(`ℹ️ ${endpoint.description}: Endpoint exists (not testing actual operations)`);
        }
      } catch (error) {
        const status = error.response?.status || 'No response';
        console.log(`❌ ${endpoint.description}: ${status} - ${error.message}`);
      }
    }
  };
  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError('');
      
      // First, load from local storage to get any persisted local changes
      console.log('💾 Loading from local storage...');
      // Local storage removed from admin dashboard
      
      console.log('📡 Fetching students from Firebase backend...');
      const response = await axios.get(`${API_BASE_URL}/api/admin/students`);
      
      if (response.data.success) {
        console.log('✅ Students fetched successfully from backend:', response.data.students.length, 'students');
        
        let backendStudents = response.data.students;
        let finalStudents = [];
        let finalArchived = [];
        
        // Local storage functionality removed for safety
        // Directly use backend data without demo fallbacks
        console.log('� Processing backend students...');
        
        // Separate backend students into active and archived
        backendStudents.forEach(student => {
          // Use archived field as primary indicator, status as fallback for legacy data
          const isArchived = student.archived === true || (student.archived === undefined && student.status === 'Archived');
          if (isArchived) {
            finalArchived.push(student);
            console.log(`📦 Backend student ${student._id || student.applicationId} is archived`);
          } else {
            finalStudents.push(student);
            console.log(`📋 Backend student ${student._id || student.applicationId} is active`);
          }
        });
        
        // Log each student's files structure for debugging
        finalStudents.forEach((student, index) => {
          console.log(`Final Student ${index} (${student.name}):`, {
            files: student.files,
            photo: student.files?.photo,
            status: student.status
          });
        });
        
        setStudents(finalStudents);
        setArchivedStudents(finalArchived);
        
        // Save the merged state back to local storage
        console.log(`💾 Final state: ${finalStudents.length} active, ${finalArchived.length} archived`);
        
      } else {
        console.error('API returned success: false');
        setError('Failed to load applications. Please try again.');
        setStudents([]);
        setArchivedStudents([]);
      }    } catch (err) {
      console.error('❌ Error fetching students:', err);
      
      // Set empty state when API fails - no demo data fallback
      console.log('❌ API connection failed - showing empty state');
      setStudents([]);
      setArchivedStudents([]);
      setError('Unable to connect to server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Set up data fetch
  useEffect(() => {
    // Setup initial data fetch
    fetchStudents();
    
    // Cleanup function
    return () => {
    };
  }, []);

  // Error state
  if (error && students.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
          : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
      }`}>
        <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-3xl p-8 shadow-2xl max-w-md mx-4`}>
          <div className="text-center">
            <MatrixLightbulb size="48" className="mx-auto mb-4 text-amber-500" />
            <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Connection Error
            </h3>
            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {error}
            </p>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              The system will automatically retry when connection is restored.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Action handlers for Accept/Reject/Archive/Delete
  const handleAcceptApplication = async (studentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [studentId]: 'accepting' }));
      console.log(`✅ Accepting application ${studentId}...`);
      
      // Try multiple API endpoints to ensure persistence
      let apiSuccess = false;
      const updateData = { 
        status: 'Approved',
        updatedAt: new Date().toISOString(),
        approvedBy: 'admin',
        approvedAt: new Date().toISOString()
      };
      
      // Try the specific status endpoint first
      try {
        console.log('📡 Trying status endpoint...');
        const response = await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}/status`, updateData);
        console.log('📡 Status endpoint response:', response.data);
        if (response.data.success) {
          apiSuccess = true;
          console.log('✅ Status endpoint worked');
        }
      } catch (statusError) {
        console.log('⚠️ Status endpoint failed:', statusError.response?.status, statusError.message);
      }
      
      // Try the general update endpoint if status endpoint failed
      if (!apiSuccess) {
        try {
          console.log('📡 Trying general update endpoint...');
          const response = await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}`, updateData);
          console.log('📡 General update response:', response.data);
          if (response.data.success) {
            apiSuccess = true;
            console.log('✅ General update endpoint worked');
          }
        } catch (updateError) {
          console.log('⚠️ General update endpoint failed:', updateError.response?.status, updateError.message);
        }
      }
      
      // Try the PATCH method if PUT failed
      if (!apiSuccess) {
        try {
          console.log('📡 Trying PATCH method...');
          const response = await axios.patch(`${API_BASE_URL}/api/admin/students/${studentId}`, updateData);
          console.log('📡 PATCH response:', response.data);
          if (response.data.success) {
            apiSuccess = true;
            console.log('✅ PATCH method worked');
          }
        } catch (patchError) {
          console.log('⚠️ PATCH method failed:', patchError.response?.status, patchError.message);
        }
      }
      
      // Always update frontend state (ensures UI works even if backend fails)
      setStudents(prev => {
        const updatedStudents = prev.map(student => {
          if (student._id === studentId || student.applicationId === studentId) {
            console.log(`✅ Updating frontend state for ${studentId} to Approved`);
            return { ...student, status: 'Approved', updatedAt: new Date().toISOString() };
          }
          return student;
        });
        
        // Local storage removed from admin dashboard
        
        return updatedStudents;
      });

      // Update Firebase Firestore directly
      let firebaseSuccess = false;
      try {
        console.log('🔥 Updating Firebase Firestore...');
        
        // Find the student document in Firebase by applicationId or _id
        const studentsRef = collection(db, 'students');
        const querySnapshot = await getDocs(studentsRef);
        
        let studentDocRef = null;
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.applicationId === studentId || data._id === studentId || doc.id === studentId) {
            studentDocRef = doc.ref;
          }
        });
        
        if (studentDocRef) {
          await updateDoc(studentDocRef, {
            status: 'Approved',
            updatedAt: new Date().toISOString(),
            approvedBy: 'admin',
            approvedAt: new Date().toISOString()
          });
          console.log('✅ Firebase Firestore updated successfully');
          firebaseSuccess = true;
        } else {
          console.log('⚠️ Student document not found in Firebase');
        }
      } catch (firebaseError) {
        console.error('❌ Error updating Firebase:', firebaseError);
      }
      
      if (apiSuccess) {
        console.log(`✅ Application ${studentId} approved successfully (persisted to backend)`);
        setError(''); // Clear any previous errors
      } else if (firebaseSuccess) {
        console.log(`✅ Application ${studentId} approved and saved to Firebase (backend offline)`);
        setError('Student approved successfully! Changes saved to Firebase.');
      } else {
        console.log(`⚠️ Application ${studentId} approved locally only (backend and Firebase sync failed)`);
        setError('Status updated locally only. Backend and Firebase sync failed.');
      }
      
    } catch (err) {
      console.error('❌ Error approving application:', err);
      setError(`Failed to approve application: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [studentId]: null }));
    }
  };

  const handleRejectApplication = async (studentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [studentId]: 'rejecting' }));
      console.log(`❌ Rejecting application ${studentId}...`);
      
      // Try multiple API endpoints to ensure persistence
      let apiSuccess = false;
      const updateData = { 
        status: 'Rejected',
        updatedAt: new Date().toISOString(),
        rejectedBy: 'admin',
        rejectedAt: new Date().toISOString()
      };
      
      // Try the specific status endpoint first
      try {
        console.log('📡 Trying status endpoint...');
        const response = await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}/status`, updateData);
        console.log('📡 Status endpoint response:', response.data);
        if (response.data.success) {
          apiSuccess = true;
          console.log('✅ Status endpoint worked');
        }
      } catch (statusError) {
        console.log('⚠️ Status endpoint failed:', statusError.response?.status, statusError.message);
      }
      
      // Try the general update endpoint if status endpoint failed
      if (!apiSuccess) {
        try {
          console.log('📡 Trying general update endpoint...');
          const response = await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}`, updateData);
          console.log('📡 General update response:', response.data);
          if (response.data.success) {
            apiSuccess = true;
            console.log('✅ General update endpoint worked');
          }
        } catch (updateError) {
          console.log('⚠️ General update endpoint failed:', updateError.response?.status, updateError.message);
        }
      }
      
      // Try the PATCH method if PUT failed
      if (!apiSuccess) {
        try {
          console.log('📡 Trying PATCH method...');
          const response = await axios.patch(`${API_BASE_URL}/api/admin/students/${studentId}`, updateData);
          console.log('📡 PATCH response:', response.data);
          if (response.data.success) {
            apiSuccess = true;
            console.log('✅ PATCH method worked');
          }
        } catch (patchError) {
          console.log('⚠️ PATCH method failed:', patchError.response?.status, patchError.message);
        }
      }
      
      // Always update frontend state (ensures UI works even if backend fails)
      setStudents(prev => {
        const updatedStudents = prev.map(student => {
          if (student._id === studentId || student.applicationId === studentId) {
            console.log(`✅ Updating frontend state for ${studentId} to Rejected`);
            return { ...student, status: 'Rejected', updatedAt: new Date().toISOString() };
          }
          return student;
        });
        
        // Save to local storage immediately after state update
        setArchivedStudents(current => {
          // Local storage removed from admin dashboard
          return current;
        });
        
        return updatedStudents;
      });

      // Update Firebase Firestore directly
      let firebaseSuccess = false;
      try {
        console.log('🔥 Updating Firebase Firestore...');
        
        // Find the student document in Firebase by applicationId or _id
        const studentsRef = collection(db, 'students');
        const querySnapshot = await getDocs(studentsRef);
        
        let studentDocRef = null;
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.applicationId === studentId || data._id === studentId || doc.id === studentId) {
            studentDocRef = doc.ref;
          }
        });
        
        if (studentDocRef) {
          await updateDoc(studentDocRef, {
            status: 'Rejected',
            updatedAt: new Date().toISOString(),
            rejectedBy: 'admin',
            rejectedAt: new Date().toISOString()
          });
          console.log('✅ Firebase Firestore updated successfully');
          firebaseSuccess = true;
        } else {
          console.log('⚠️ Student document not found in Firebase');
        }
      } catch (firebaseError) {
        console.error('❌ Error updating Firebase:', firebaseError);
      }
      
      if (apiSuccess) {
        console.log(`✅ Application ${studentId} rejected successfully (persisted to backend)`);
        setError(''); // Clear any previous errors
      } else if (firebaseSuccess) {
        console.log(`✅ Application ${studentId} rejected and saved to Firebase (backend offline)`);
        setError('Student rejected successfully! Changes saved to Firebase.');
      } else {
        console.log(`⚠️ Application ${studentId} rejected locally only (backend and Firebase sync failed)`);
        setError('Status updated locally only. Backend and Firebase sync failed.');
      }
      
    } catch (err) {
      console.error('❌ Error rejecting application:', err);
      setError(`Failed to reject application: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [studentId]: null }));
    }
  };

  const handleArchiveApplication = async (studentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [studentId]: 'archiving' }));
      console.log(`📦 Moving application ${studentId} to archive (safe delete)...`);
      
      // Find the student to archive before making API call
      const studentToArchive = students.find(s => s._id === studentId || s.applicationId === studentId);
      console.log('Student to archive:', studentToArchive);
      
      if (!studentToArchive) {
        throw new Error('Student not found in active applications');
      }
      
      // Try to update status to 'Archived' in backend (DON'T delete - we want to keep for other devices)
      let backendArchiveSuccess = false;
      const archiveData = {
        status: 'Archived',
        archived: true,
        archivedAt: new Date().toISOString(),
        originalStatus: studentToArchive.status || 'Pending',
        archivedReason: 'Moved to archive via admin action'
      };
      
      try {
        console.log('📡 Attempting to mark as archived in backend...');
        
        // Try multiple backend endpoints to ensure the archive status is saved
        let archiveResponse = null;
        
        // Try specific status endpoint first
        try {
          archiveResponse = await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}/status`, archiveData);
          if (archiveResponse.data && archiveResponse.data.success) {
            backendArchiveSuccess = true;
            console.log('✅ Student marked as archived via status endpoint');
          }
        } catch (statusError) {
          console.log('⚠️ Status endpoint failed, trying general update...');
          
          // Fallback to general update endpoint
          try {
            archiveResponse = await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}`, archiveData);
            if (archiveResponse.data && archiveResponse.data.success) {
              backendArchiveSuccess = true;
              console.log('✅ Student marked as archived via general update endpoint');
            }
          } catch (updateError) {
            console.log('⚠️ General update failed, trying PATCH...');
            
            // Final fallback to PATCH
            try {
              archiveResponse = await axios.patch(`${API_BASE_URL}/api/admin/students/${studentId}`, archiveData);
              if (archiveResponse.data && archiveResponse.data.success) {
                backendArchiveSuccess = true;
                console.log('✅ Student marked as archived via PATCH endpoint');
              }
            } catch (patchError) {
              console.log('⚠️ All backend archive attempts failed');
            }
          }
        }
        
      } catch (archiveError) {
        console.log('⚠️ Backend archive failed:', archiveError.response?.status, archiveError.message);
        console.log('📡 All backend archive endpoints failed - will save locally and try to sync later');
        // Continue with local archiving even if backend fails
      }
      
      // ALWAYS perform local archiving (ensures UI responsiveness)
      const archivedStudent = { 
        ...studentToArchive, 
        originalStatus: studentToArchive.status || 'Pending',
        status: 'Archived',
        archived: true,
        archivedAt: new Date().toISOString(),
        archivedReason: 'Moved to archive via admin action',
        backendArchived: backendArchiveSuccess // Track if backend was updated
      };
      
      // Add to archived students immediately (UI update)
      setArchivedStudents(prev => {
        console.log('✅ Adding to archived students:', archivedStudent);
        const updatedArchived = [...prev, archivedStudent];
        
        // Save to local storage after updating archive
        setStudents(current => {
          const updatedStudents = current.filter(s => s._id !== studentId && s.applicationId !== studentId);
          // Local storage removed from admin dashboard
          return updatedStudents;
        });
        
        return updatedArchived;
      });

      // Update Firebase Firestore directly
      try {
        console.log('🔥 Updating Firebase Firestore for archive...');
        
        // Find the student document in Firebase by applicationId or _id
        const studentsRef = collection(db, 'students');
        const querySnapshot = await getDocs(studentsRef);
        
        let studentDocRef = null;
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.applicationId === studentId || data._id === studentId || doc.id === studentId) {
            studentDocRef = doc.ref;
          }
        });
        
        if (studentDocRef) {
          await updateDoc(studentDocRef, {
            status: 'Archived',
            archived: true,
            archivedAt: new Date().toISOString(),
            originalStatus: studentToArchive.status || 'Pending',
            archivedReason: 'Moved to archive via admin action',
            updatedAt: new Date().toISOString()
          });
          console.log('✅ Firebase Firestore updated successfully for archive');
        } else {
          console.log('⚠️ Student document not found in Firebase for archive');
        }
      } catch (firebaseError) {
        console.error('❌ Error updating Firebase for archive:', firebaseError);
      }
      
      if (backendArchiveSuccess) {
        console.log(`✅ Application ${studentId} archived successfully (status saved to backend - will sync across all devices)`);
        setError(''); // Clear any previous errors
      } else {
        console.log(`⚠️ Application ${studentId} archived locally only (backend sync failed)`);
        console.log('💾 Local storage will preserve the archived state across browser refreshes');
        console.log('🔄 Next time you refresh, the archived student should still appear in the archive');
        setError('Archived locally. Backend sync failed but local storage will preserve the archive state.');
      }
      
    } catch (err) {
      console.error('❌ Error moving application to archive:', err);
      setError(`Failed to move application to archive: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [studentId]: null }));
    }
  };

  const handlePermanentDelete = async (studentId) => {
    // Double confirmation for permanent delete
    const firstConfirm = window.confirm('⚠️ PERMANENT DELETE WARNING ⚠️\n\nThis will permanently remove this application from the system.\n\nThe student can still reapply later, but all current data will be lost.\n\nAre you sure you want to continue?');
    if (!firstConfirm) return;
    
    const finalConfirm = window.confirm('FINAL CONFIRMATION:\n\nThis action CANNOT be undone.\n\nClick OK to permanently delete this application.');
    if (!finalConfirm) return;
    
    try {
      setActionLoading(prev => ({ ...prev, [studentId]: 'deleting' }));
      console.log(`🗑️ PERMANENTLY deleting application ${studentId} from system...`);
      
      // Find the student being deleted for logging
      const studentToDelete = archivedStudents.find(s => s._id === studentId || s.applicationId === studentId);
      if (studentToDelete) {
        console.log('Student being permanently deleted:', {
          name: studentToDelete.name,
          email: studentToDelete.email,
          applicationId: studentToDelete.applicationId || studentToDelete._id,
          originalStatus: studentToDelete.originalStatus,
          archivedAt: studentToDelete.archivedAt
        });
      }
      
      // Always perform local deletion FIRST (ensures UI responsiveness)
      setArchivedStudents(prev => {
        const updated = prev.filter(s => s._id !== studentId && s.applicationId !== studentId);
        console.log(`✅ Removed from archived students. Remaining archived: ${updated.length}`);
        
        // Save to local storage immediately after permanent deletion
        setStudents(current => {
          // Local storage removed from admin dashboard
          return current;
        });
        
        return updated;
      });
      
      console.log(`✅ Application ${studentId} permanently deleted from system`);
      console.log('ℹ️ Student can still reapply with a new application if needed');
      setError(''); // Clear any previous errors
      
      // Try to sync with backend - ALWAYS attempt delete for permanent removal
      try {
        console.log('📡 Syncing permanent deletion to backend...');
        const response = await axios.delete(`${API_BASE_URL}/api/admin/students/${studentId}`);
        if (response.data && response.data.success) {
          console.log('📡 Backend deletion sync successful - permanently removed from all devices');
        } else {
          console.log('📡 Backend deletion attempted but response unclear');
        }
      } catch (apiError) {
        console.log('📡 Backend deletion sync failed:', apiError.response?.status, apiError.message);
        setError('Deleted locally. Backend sync failed - student may still appear on other devices.');
      }
      
    } catch (err) {
      console.error('❌ Error permanently deleting application:', err);
      setError(`Failed to permanently delete application: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [studentId]: null }));
    }
  };

  const handleRestoreFromArchive = async (studentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [studentId]: 'restoring' }));
      console.log(`🔄 Restoring application ${studentId} from archive...`);
      
      // Find the student to restore
      const studentToRestore = archivedStudents.find(s => s._id === studentId || s.applicationId === studentId);
      
      if (!studentToRestore) {
        throw new Error('Student not found in archived applications');
      }
      
      console.log(`🔄 Resetting status and making actionable again (was: ${studentToRestore.status})`);
      console.log('Student to restore:', {
        name: studentToRestore?.name,
        currentStatus: studentToRestore?.status,
        willBecome: 'Pending'
      });
      
      // Always perform local restoration FIRST (ensures UI responsiveness)
      const { archivedAt, archived, archivedReason, originalStatus, ...restoredStudent } = studentToRestore;
      
      // Always reset to Pending status when restoring (so admin can approve/reject again)
      restoredStudent.status = 'Pending';
      restoredStudent.restoredAt = new Date().toISOString();
      restoredStudent.restoredBy = 'admin';
      restoredStudent.lastOperation = 'restore';
      restoredStudent.lastOperationTimestamp = new Date().toISOString();
      
      console.log(`✅ Status reset: ${studentToRestore.status} → Pending (admin can now approve/reject again)`);
      
      // Calculate the new states first to update local storage immediately
      const updatedStudents = [...students, restoredStudent];
      const updatedArchived = archivedStudents.filter(s => s._id !== studentId && s.applicationId !== studentId);
      
      // Update local storage immediately to prevent race conditions with Firebase listener
      // Local storage removed from admin dashboard
      console.log('💾 Local storage updated immediately for restore operation');
      
      // Remove from archived students first
      setArchivedStudents(prev => {
        const newArchived = prev.filter(s => s._id !== studentId && s.applicationId !== studentId);
        console.log('✅ Removed from archived students:', studentId, 'New archived count:', newArchived.length);
        return newArchived;
      });
      
      // Add back to active students
      setStudents(prev => {
        const newStudents = [...prev, restoredStudent];
        console.log('✅ Added back to active students:', restoredStudent.name, 'New active count:', newStudents.length);
        return newStudents;
      });
      
      console.log(`✅ Application ${studentId} restored from archive to active applications`);
      setError(''); // Clear any previous errors
      
      // Update Firebase Firestore directly for real-time sync
      try {
        console.log('🔥 Updating Firebase Firestore for restore...');
        const studentDoc = doc(db, 'students', studentId);
        await updateDoc(studentDoc, {
          status: 'Pending',
          archived: false,
          archivedAt: null,
          archivedReason: null,
          originalStatus: null, // Clear the original status so it doesn't affect future operations
          restoredAt: new Date().toISOString(),
          restoredBy: 'admin',
          updatedAt: new Date().toISOString(),
          // Add a flag to indicate this was a restore operation for Firebase listeners
          lastOperation: 'restore',
          lastOperationTimestamp: new Date().toISOString()
        });
        console.log('✅ Firebase Firestore updated successfully for restore - student reset to Pending status');
        setError('Application restored and reset to Pending status. Student can now be approved/rejected again.');
      } catch (firestoreError) {
        console.log('⚠️ Firebase Firestore restore update failed (continuing with backend sync):', firestoreError.message);
        setError('Restored locally. Firebase sync failed - may not appear on other devices immediately.');
      }
      
      // Try to sync with backend (but don't fail if it doesn't work)
      try {
        console.log('📡 Syncing restore status to backend...');
        await axios.put(`${API_BASE_URL}/api/admin/students/${studentId}/status`, {
          status: 'Pending',
          archived: false,
          originalStatus: null,
          restoredAt: new Date().toISOString(),
          restoredBy: 'admin',
          lastOperation: 'restore',
          lastOperationTimestamp: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log('📡 Backend restore sync successful - student reset to Pending status');
      } catch (apiError) {
        console.log('📡 Backend restore sync failed (app works in offline mode):', apiError.message);
        // Don't show error to user - local restore was successful and Firebase was updated
      }
      
    } catch (err) {
      console.error('❌ Error restoring application from archive:', err);
      setError(`Failed to restore application: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [studentId]: null }));
    }
  };

  // Sort by date descending (latest first)
  const sortByDateDesc = (a, b) => {
    const dateA = new Date(a.createdAt || a.submittedAt || 0).getTime();
    const dateB = new Date(b.createdAt || b.submittedAt || 0).getTime();
    return dateB - dateA;
  };

  // Filter students based on search and status
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.applicationId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || student.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).sort(sortByDateDesc);

  const filteredArchivedStudents = archivedStudents.filter(student => {
    const matchesSearch = student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.applicationId?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }).sort(sortByDateDesc);

  return (
    <>
      {/* Modern Glassmorphism Background */}
      <div className={`min-h-screen relative overflow-hidden ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
          : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
      }`}>
        
        {/* Animated Background */}
        <div className="fixed inset-0 z-0">
          <div className={`w-full h-full ${
            darkMode 
              ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
              : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
          }`} />
          <div className="floating-blob" />
          <div className="floating-blob" />
          <div className="floating-blob" />
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex-grow py-4 sm:py-6 lg:py-8 px-3 sm:px-4">
          <div className="container mx-auto max-w-7xl">
            
            {/* Header */}
            <div className="mb-4 sm:mb-6 lg:mb-8">
              <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl sm:rounded-2xl lg:rounded-3xl p-3 sm:p-4 lg:p-6 xl:p-8 shadow-2xl`}>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
                    <div className="relative flex-shrink-0">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center text-white shadow-lg">
                        <MatrixAdmin size="32" className="sm:hidden" />
                        <MatrixAdmin size="40" className="hidden sm:block" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-green-400 rounded-full border-2 border-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r ${
                        darkMode 
                          ? 'from-white to-gray-300' 
                          : 'from-gray-900 to-gray-600'
                      } bg-clip-text text-transparent break-words`}>
                        Admin Dashboard
                      </h1>
                      <p className={`text-sm sm:text-base lg:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} break-words`}>
                        Manage MBBS applications with modern elegance
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 md:gap-3">
                    {/* All three buttons in a responsive grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                      {/* Partner Registration Button */}
                      <button
                        onClick={() => navigate('/admin/partner-registration')}
                        className={`px-2 md:px-3 lg:px-4 py-2 md:py-3 rounded-lg md:rounded-xl lg:rounded-2xl font-medium text-xs md:text-sm transition-all duration-300 hover:scale-105 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg border-0 focus:outline-none focus:ring-2 focus:ring-green-300`}
                      >
                        <div className="flex flex-col md:flex-row items-center gap-1 md:gap-2 text-white">
                          <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="text-white font-semibold text-center leading-tight">
                            <span className="block md:hidden">Register</span>
                            <span className="hidden md:block lg:inline">Partner Registration</span>
                          </span>
                        </div>
                      </button>

                      {/* Partners Refers Button */}
                      <button
                        onClick={() => navigate('/admin/partners-management')}
                        className={`px-2 md:px-3 lg:px-4 py-2 md:py-3 rounded-lg md:rounded-xl lg:rounded-2xl font-medium text-xs md:text-sm transition-all duration-300 hover:scale-105 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-300`}
                      >
                        <div className="flex flex-col md:flex-row items-center gap-1 md:gap-2 text-white">
                          <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-white font-semibold text-center leading-tight">
                            <span className="block md:hidden">Refers</span>
                            <span className="hidden md:block lg:inline">Partners Refers</span>
                          </span>
                        </div>
                      </button>

                      {/* Clear Cache Button */}
                      <button
                        onClick={() => {
                          if (window.confirm('Clear cached data and sync with Firebase? This will reload the page.')) {
                            // clearLocalStorage removed();
                            window.location.reload();
                          }
                        }}
                        className={`col-span-2 md:col-span-1 px-2 md:px-3 lg:px-4 py-2 md:py-3 rounded-lg md:rounded-xl lg:rounded-2xl font-medium text-xs md:text-sm transition-all duration-300 hover:scale-105 ${
                          darkMode 
                            ? 'bg-orange-600 hover:bg-orange-700 text-white border border-orange-500' 
                            : 'bg-orange-500 hover:bg-orange-600 text-white border border-orange-400'
                        }`}
                        title="Clear local cache and sync with Firebase"
                      >
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                          <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="text-white font-medium">
                            <span className="md:hidden">Clear Cache</span>
                            <span className="hidden md:inline">Clear Cache</span>
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
              {/* Pending Applications */}
              <button
                onClick={() => setStatusFilter(statusFilter === 'Pending' ? '' : 'Pending')}
                className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer ${
                  statusFilter === 'Pending' ? 'ring-2 ring-yellow-400 bg-yellow-400/10' : ''
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                    <MatrixClock size="24" className="sm:hidden" />
                    <MatrixClock size="28" className="hidden sm:block" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className={`text-2xl sm:text-3xl font-bold ${
                      darkMode ? 'text-yellow-300' : 'text-yellow-600'
                    }`}>
                      {students.filter(s => s.status === 'Pending').length}
                    </p>
                    <p className={`text-xs sm:text-sm font-medium ${
                      darkMode ? 'text-white/70' : 'text-gray-600'
                    } text-break`}>
                      Pending {statusFilter === 'Pending' ? '(Active Filter)' : ''}
                    </p>
                  </div>
                </div>
              </button>

              {/* Approved Applications */}
              <button
                onClick={() => setStatusFilter(statusFilter === 'Approved' ? '' : 'Approved')}
                className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer ${
                  statusFilter === 'Approved' ? 'ring-2 ring-green-400 bg-green-400/10' : ''
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                    <MatrixCheckCircle size="24" className="sm:hidden" />
                    <MatrixCheckCircle size="28" className="hidden sm:block" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className={`text-2xl sm:text-3xl font-bold ${
                      darkMode ? 'text-green-300' : 'text-green-600'
                    }`}>
                      {students.filter(s => s.status === 'Approved').length}
                    </p>
                    <p className={`text-xs sm:text-sm font-medium ${
                      darkMode ? 'text-white/70' : 'text-gray-600'
                    } text-break`}>
                      Approved {statusFilter === 'Approved' ? '(Active Filter)' : ''}
                    </p>
                  </div>
                </div>
              </button>

              {/* Rejected Applications (includes archived) */}
              <button
                onClick={() => setStatusFilter(statusFilter === 'Rejected' ? '' : 'Rejected')}
                className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer sm:col-span-2 lg:col-span-1 ${
                  statusFilter === 'Rejected' ? 'ring-2 ring-red-400 bg-red-400/10' : ''
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                    <MatrixXCircle size="24" className="sm:hidden" />
                    <MatrixXCircle size="28" className="hidden sm:block" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className={`text-2xl sm:text-3xl font-bold ${
                      darkMode ? 'text-rose-300' : 'text-rose-600'
                    }`}>
                      {students.filter(s => s.status === 'Rejected').length + archivedStudents.length}
                    </p>
                    <p className={`text-xs sm:text-sm font-medium ${
                      darkMode ? 'text-white/70' : 'text-gray-600'
                    } text-break`}>
                      Rejected {statusFilter === 'Rejected' ? '(Active Filter)' : ''}
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Debug Information (always visible with toggle) */}
            {debugMode && (
              <div className={`mb-6 p-4 rounded-2xl backdrop-blur-2xl border ${
                darkMode 
                  ? 'bg-blue-500/10 border-blue-400/20 text-blue-300' 
                  : 'bg-blue-50/70 border-blue-200/50 text-blue-800'
              } shadow-xl`}>
                <div className="text-sm">
                  <h4 className="font-semibold mb-2">🐛 Debug Info:</h4>
                  <p>Active Students: {students.length}</p>
                  <p>Archived Students: {archivedStudents.length}</p>
                  <p>Current View: {currentView}</p>
                  <p>API URL: {API_BASE_URL}</p>
                  {Object.keys(actionLoading).length > 0 && (
                    <p>Loading Actions: {JSON.stringify(actionLoading)}</p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => {
                        console.log('🐛 Current students:', students);
                        console.log('🐛 Current archived students:', archivedStudents);
                        console.log('🐛 Action loading state:', actionLoading);
                      }}
                      className={`px-3 py-1 rounded-lg text-xs ${
                        darkMode 
                          ? 'bg-blue-600/30 hover:bg-blue-600/50 text-blue-200' 
                          : 'bg-blue-200 hover:bg-blue-300 text-blue-800'
                      }`}
                    >
                      Log State to Console
                    </button>
                    <button
                      onClick={testBackendEndpoints}
                      className={`px-3 py-1 rounded-lg text-xs ${
                        darkMode 
                          ? 'bg-green-600/30 hover:bg-green-600/50 text-green-200' 
                          : 'bg-green-200 hover:bg-green-300 text-green-800'
                      }`}
                    >
                      Test Backend APIs
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message with Glassmorphism */}
            {error && (
              <div className={`mb-8 p-6 rounded-3xl backdrop-blur-2xl border ${
                error.includes('successfully') || error.includes('Firebase')
                  ? darkMode 
                    ? 'bg-green-500/10 border-green-400/20 text-green-300' 
                    : 'bg-green-50/70 border-green-200/50 text-green-800'
                  : darkMode 
                    ? 'bg-amber-500/10 border-amber-400/20 text-amber-300' 
                    : 'bg-amber-50/70 border-amber-200/50 text-amber-800'
              } shadow-xl`}>
                <div className="flex items-center space-x-3">
                  {error.includes('successfully') || error.includes('Firebase') ? (
                    <MatrixCheckCircle size="24" className="text-green-500 flex-shrink-0" />
                  ) : (
                    <MatrixLightbulb size="24" className="text-amber-500 flex-shrink-0" />
                  )}
                  <div>
                    <h4 className="font-semibold">
                      {error.includes('successfully') || error.includes('Firebase') 
                        ? 'Status Update' 
                        : 'Working Offline'
                      }
                    </h4>
                    <p className="text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Applications Table */}
            <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden`}>
              <div className="p-3 sm:p-4 lg:p-6 border-b border-white/10">
                <div className="flex flex-col gap-4">
                  {/* Header and View Toggle */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {currentView === 'active' ? 'Active Applications' : 'Archived Applications'}
                      </h2>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {currentView === 'active' 
                          ? `${filteredStudents.length} of ${students.length} applications`
                          : `${filteredArchivedStudents.length} of ${archivedStudents.length} archived applications`
                        }
                      </p>
                    </div>
                    
                    {/* View Toggle and Debug Toggle */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <div className={`flex rounded-xl p-1 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                        <button
                          onClick={() => setCurrentView('active')}
                          className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 touch-manipulation ${
                            currentView === 'active'
                              ? darkMode 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-white text-blue-600 shadow-lg'
                              : darkMode 
                                ? 'text-gray-300 hover:text-white' 
                                : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <MatrixGraduationCap size="16" className="inline mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Active </span>({students.length})
                        </button>
                        <button
                          onClick={() => setCurrentView('archive')}
                          className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 touch-manipulation ${
                            currentView === 'archive'
                              ? darkMode 
                                ? 'bg-orange-600 text-white shadow-lg' 
                                : 'bg-white text-orange-600 shadow-lg'
                              : darkMode 
                                ? 'text-gray-300 hover:text-white' 
                                : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <MatrixTrash size="16" className="inline mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Archive </span>({archivedStudents.length})
                        </button>
                      </div>
                      
                    </div>
                  </div>
                  
                  {/* Search and Filter */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <div className="relative flex-1 sm:max-w-md">
                      <input
                        type="text"
                        placeholder="Search applications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`apple-input w-full px-4 py-3 sm:py-2 rounded-xl text-sm ${
                          darkMode 
                            ? 'text-white placeholder-gray-400' 
                            : 'text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-0 touch-manipulation`}
                      />
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    
                    {currentView === 'active' && (
                      <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`apple-input px-4 py-3 sm:py-2 rounded-xl text-sm ${
                          darkMode 
                            ? 'text-white' 
                            : 'text-gray-900'
                        } focus:outline-none focus:ring-0 touch-manipulation min-w-0`}
                      >
                        <option value="">All Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    )}
                    
                    {/* Clear Search Button */}
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className={`px-4 py-3 sm:py-2 rounded-xl text-sm font-medium transition-all duration-200 touch-manipulation ${
                          darkMode 
                            ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Spinner />
                  <p className={`mt-4 text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Loading applications...
                  </p>
                </div>
              ) : (
                /* Applications Grid */
                <div className="p-3 sm:p-4 lg:p-6">
                  {(() => {
                    const currentData = currentView === 'active' ? filteredStudents : filteredArchivedStudents;
                    const emptyMessage = currentView === 'active' 
                      ? 'No applications found' 
                      : 'No archived applications found';
                    const emptyDescription = currentView === 'active'
                      ? 'Applications will appear here once students start submitting.'
                      : 'Archived applications will appear here when you archive active applications.';

                    if (currentData.length === 0) {
                      return (
                        <div className="text-center py-16">
                          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            {currentView === 'active' ? (
                              <MatrixGraduationCap size="40" className="text-gray-500" />
                            ) : (
                              <MatrixTrash size="40" className="text-gray-500" />
                            )}
                          </div>
                          <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {emptyMessage}
                          </h3>
                          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {emptyDescription}
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                        {currentData.map((student, index) => (
                          <ApplicationCard 
                            key={student._id || student.applicationId || index} 
                            student={student} 
                            darkMode={darkMode}
                            isArchived={currentView === 'archive'}
                            actionLoading={actionLoading}
                            onViewDetails={(student) => {
                              setSelectedStudent(student);
                              setIsStudentModalOpen(true);
                            }}
                            onAccept={handleAcceptApplication}
                            onReject={handleRejectApplication}
                            onArchive={handleArchiveApplication}
                            onRestore={handleRestoreFromArchive}
                            onPermanentDelete={handlePermanentDelete}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Student Details Modal */}
      <StudentDetailsModal
        student={selectedStudent}
        darkMode={darkMode}
        isOpen={isStudentModalOpen}
        onClose={() => setIsStudentModalOpen(false)}
        setSelectedImage={setSelectedImage}
        setImageModalOpen={setImageModalOpen}
        setSelectedPdf={setSelectedPdf}
        setPdfModalOpen={setPdfModalOpen}
      />

      {/* Enhanced Image Modal */}
      <EnhancedImageModal
        image={selectedImage}
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        darkMode={darkMode}
      />

      {/* PDF Modal */}
      <PdfModal
        pdf={selectedPdf}
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        darkMode={darkMode}
      />
    </>
  );
};

export default AdminDashboardPage;
